import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { api } from "../../lib/api";
import { useConfirm } from "../../components/ConfirmProvider";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  contact_person: "",
  address: "",
  country: "Schweiz",
  website: "",
  note: "",
};

/**
 * CRUD furnizori inventar.
 * Props:
 *  - open, onClose, onChanged (callback la modificări — refresh comenzi)
 */
export default function SuppliersDialog({ open, onClose, onChanged }) {
  const confirm = useConfirm();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null sau supplier
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!open) return;
    setLoading(true);
    try {
      const r = await api.get("/inventory/suppliers");
      setSuppliers(r.data || []);
    } catch {
      toast.error("Eroare la încărcarea furnizorilor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

  const startNew = () => {
    setEditing({ __new: true });
    setForm({ ...EMPTY });
  };

  const startEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "",
      email: s.email || "",
      phone: s.phone || "",
      contact_person: s.contact_person || "",
      address: s.address || "",
      country: s.country || "Schweiz",
      website: s.website || "",
      note: s.note || "",
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({ ...EMPTY });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Numele furnizorului e obligatoriu"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        contact_person: form.contact_person.trim() || null,
        address: form.address.trim() || null,
        country: form.country.trim() || "Schweiz",
        website: form.website.trim() || null,
        note: form.note.trim() || null,
      };
      if (editing?.__new) {
        await api.post("/inventory/suppliers", payload);
        toast.success("✓ Furnizor adăugat");
      } else {
        await api.put(`/inventory/suppliers/${editing.id}`, payload);
        toast.success("✓ Furnizor actualizat");
      }
      cancelEdit();
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setBusy(false);
    }
  };

  const delSupplier = async (s) => {
    const ok = await confirm({
      title: "Șterge furnizorul",
      body: `Ești sigur că vrei să ștergi furnizorul „${s.name}"?`,
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/inventory/suppliers/${s.id}`);
      toast.success("Furnizor șters");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="suppliers-dialog">
        <DialogHeader>
          <DialogTitle>🏢 Furnizori</DialogTitle>
        </DialogHeader>

        {!editing && (
          <div className="flex items-center justify-between border-b border-zinc-200 pb-2 mb-2">
            <div className="text-xs text-zinc-500">{suppliers.length} furnizori înregistrați</div>
            <Button size="sm" onClick={startNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="supplier-add-btn">
              ➕ Adaugă furnizor
            </Button>
          </div>
        )}

        {editing && (
          <div className="border-2 border-emerald-300 bg-emerald-50/50 rounded-lg p-3 mb-3">
            <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-800 mb-2">
              {editing.__new ? "➕ Furnizor nou" : `✎ Editează „${editing.name}"`}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="small-label">🏢 Nume *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="ex: Hilti AG"
                  data-testid="supplier-name-input"
                />
              </div>
              <div>
                <Label className="small-label">📧 Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="info@firma.de"
                  data-testid="supplier-email-input"
                />
              </div>
              <div>
                <Label className="small-label">📞 Telefon</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+41 26 350 12 12"
                  data-testid="supplier-phone-input"
                />
              </div>
              <div>
                <Label className="small-label">👤 Persoană de contact</Label>
                <Input
                  value={form.contact_person}
                  onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                  placeholder="ex: Herr Müller"
                  data-testid="supplier-contact-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="small-label">📍 Adresă</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="ex: Industriestrasse 4, 6286 Altdorf"
                  data-testid="supplier-address-input"
                />
              </div>
              <div>
                <Label className="small-label">🌍 Țară</Label>
                <Input
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  data-testid="supplier-country-input"
                />
              </div>
              <div>
                <Label className="small-label">🔗 Website</Label>
                <Input
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://hilti.ch"
                  data-testid="supplier-website-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="small-label">📝 Notă</Label>
                <Textarea
                  rows={2}
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="ex: Termen plată 30 zile · livrare 5-7 zile lucrătoare"
                  data-testid="supplier-note-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={cancelEdit} disabled={busy}>Anulează</Button>
              <Button
                size="sm"
                onClick={save}
                disabled={busy || !form.name.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="supplier-save-btn"
              >
                {busy ? "Salvez…" : "💾 Salvează"}
              </Button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto" data-testid="suppliers-list">
          {loading ? (
            <div className="text-center text-sm text-zinc-400 py-8">Se încarcă…</div>
          ) : suppliers.length === 0 ? (
            <div className="text-center text-sm text-zinc-400 italic py-8">
              Nu ai înregistrat încă niciun furnizor. Apasă „➕ Adaugă furnizor" pentru a începe.
            </div>
          ) : (
            <div className="space-y-2">
              {suppliers.map((s) => (
                <div
                  key={s.id}
                  className="border border-zinc-200 rounded-md p-3 bg-white hover:border-zinc-300"
                  data-testid={`supplier-row-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-zinc-900">{s.name}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 space-y-0.5">
                        {s.contact_person && <div>👤 {s.contact_person}</div>}
                        {s.email && <div>📧 <a href={`mailto:${s.email}`} className="text-blue-600 hover:underline">{s.email}</a></div>}
                        {s.phone && <div>📞 {s.phone}</div>}
                        {s.address && <div>📍 {s.address}{s.country ? `, ${s.country}` : ""}</div>}
                        {s.website && <div>🔗 <a href={s.website.startsWith("http") ? s.website : `https://${s.website}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{s.website}</a></div>}
                      </div>
                      {s.note && <div className="text-[11px] italic text-zinc-600 mt-1 bg-zinc-50 rounded px-2 py-1">📝 {s.note}</div>}
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => startEdit(s)}
                        data-testid={`supplier-edit-${s.id}`}
                      >
                        ✎ Editează
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => delSupplier(s)}
                        data-testid={`supplier-delete-${s.id}`}
                      >
                        🗑 Șterge
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
